import React, { useState } from 'react';
import { Plus, Users, Globe, AlertCircle, X, Loader2 } from 'lucide-react';
import SourceCard from './SourceCard';
import * as fbMonitorApi from '../../api/fbMonitor';

const SourcesManager = ({
  sources,
  sourcesLimit,
  onSourceAdded,
  onSourceUpdated,
  onSourceDeleted,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSource, setNewSource] = useState({ url: '', name: '', type: 'group' });
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  const canAddMore = sources.length < sourcesLimit;

  const validateFacebookUrl = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname.includes('facebook.com');
    } catch {
      return false;
    }
  };

  const handleAddSource = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate URL
    const trimmedUrl = newSource.url.trim();
    if (!trimmedUrl) {
      setError('Please enter a Facebook URL');
      return;
    }

    if (!validateFacebookUrl(trimmedUrl)) {
      setError('Please enter a valid Facebook URL');
      return;
    }

    setIsAdding(true);
    try {
      const added = await fbMonitorApi.addSource({
        url: trimmedUrl,
        name: newSource.name.trim() || undefined,
        type: newSource.type,
      });
      onSourceAdded(added);
      setShowAddModal(false);
      setNewSource({ url: '', name: '', type: 'group' });
    } catch (err) {
      setError(err.message || 'Failed to add source');
    } finally {
      setIsAdding(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setNewSource({ url: '', name: '', type: 'group' });
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold dark:text-white text-slate-900">
            Monitored Sources
          </h2>
          <p className="text-sm dark:text-slate-400 text-slate-500">
            {sources.length} of {sourcesLimit} sources used
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={!canAddMore}
          className="px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Source
        </button>
      </div>

      {/* Progress Bar */}
      <div className="dark:bg-slate-800 bg-slate-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-sky-500 transition-all duration-300"
          style={{ width: `${(sources.length / sourcesLimit) * 100}%` }}
        />
      </div>

      {/* Sources List */}
      {sources.length === 0 ? (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-8 text-center">
          <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            No Sources Yet
          </h3>
          <p className="dark:text-slate-400 text-slate-500 mb-4 max-w-md mx-auto">
            Add Facebook groups or pages to monitor. We'll scan them daily and send you a report with potential mortgage leads.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-semibold inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Your First Source
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {sources.map((source) => (
            <SourceCard
              key={source.id}
              source={source}
              onUpdated={onSourceUpdated}
              onDeleted={onSourceDeleted}
            />
          ))}
        </div>
      )}

      {/* Add Source Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-slate-800 border-slate-200">
              <h3 className="text-lg font-semibold dark:text-white text-slate-900">
                Add Source
              </h3>
              <button
                onClick={closeModal}
                className="p-1 text-slate-400 hover:text-slate-300 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleAddSource} className="p-4 space-y-4">
              {/* Source Type */}
              <div>
                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                  Source Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewSource(prev => ({ ...prev, type: 'group' }))}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                      newSource.type === 'group'
                        ? 'bg-sky-500/10 border-sky-500 text-sky-500'
                        : 'dark:bg-slate-800 bg-slate-50 dark:border-slate-700 border-slate-200 dark:text-slate-400 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Users className="w-5 h-5" />
                    Group
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewSource(prev => ({ ...prev, type: 'page' }))}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                      newSource.type === 'page'
                        ? 'bg-sky-500/10 border-sky-500 text-sky-500'
                        : 'dark:bg-slate-800 bg-slate-50 dark:border-slate-700 border-slate-200 dark:text-slate-400 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Globe className="w-5 h-5" />
                    Page
                  </button>
                </div>
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                  Facebook URL <span className="text-red-400">*</span>
                </label>
                <input
                  type="url"
                  value={newSource.url}
                  onChange={(e) => setNewSource(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://facebook.com/groups/..."
                  className="w-full rounded-lg px-3 py-2 focus:border-sky-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900"
                  required
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                  Display Name <span className="text-slate-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newSource.name}
                  onChange={(e) => setNewSource(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Miami Real Estate Group"
                  className="w-full rounded-lg px-3 py-2 focus:border-sky-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm dark:text-red-400 text-red-600">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 dark:text-slate-400 text-slate-600 hover:dark:bg-slate-800 hover:bg-slate-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAdding || !newSource.url.trim()}
                  className="flex-1 px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  {isAdding ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Source
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcesManager;
